import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import useUIHelper from "../../../store/uiStore/useUIHelper";
import { usePlanStore } from "../../../store/PlanStore";

import planFactory from "../../../store/factory/planFactory";
import CalculationAnimate from "../popup/CalculationAnimate";
import ErrorPopup from "../popup/ErrorPopup";

import RedirectLink from "../../../components/redirect/RedirectLink";

import { ReactComponent as NotFoundSVG } from "../../../assets/not-found.svg";
import { ReactComponent as MagnifierSVG } from "../../../assets/magnifier.svg";

import { getPlanerURL } from "../../../utils/path.utils";

import calcApi from "../../../service/calcApi";
import resourceApi from "../../../service/resourceApi";

import usePolling from "../../../hooks/usePolling";
import useFetch from "../../../hooks/useFetch";
import hydrawiseSettingsFactory from "../../../store/factory/hydrawiseSettingsFactory";
import { injectCircuitsWithDefaults } from "./properties/controller/sensor-settings/ValvesSettingsTypes";
import { useUserStore } from "../../../store/UserStore";

function PlanDataController() {
  const { formatMessage } = useIntl();

  const userStore = useUserStore();

  const {
    planId,
    data,
    sensorList,
    sensorSerialNumbers,
    changeSensorData,
    waterFilterSerialNumber,
    changeWaterFilterData,
    changeData,
    changeSoilNotificationEnabledValue,
    changeSensorProperties,
    changeHydrawiseSettings,
    changeWaterFilterProperties,
    fertilizerSerialNumber,
    changeFertilizerData,
    changeFertilizerProperties,
  } = usePlanStore();

  const { zoomToElements } = useUIHelper();

  const [initialZoomEnabled, changeInitalZoomEnabled] = React.useState(true);

  const fetchPlanData = React.useCallback(async () => {
    if (planId == null) throw new Error("Plan id is not empty");

    const planData = await resourceApi.fetchPlan(planId);
    changeData(planFactory(planData));
  }, [changeData, planId]);

  const fetchNotificationProperties = React.useCallback(async () => {
    if (planId == null) throw new Error("Plan id is not empty");

    const res = await calcApi.fetchNotificationProperties(
      planId,
      userStore?.requestLogin
    );
    changeSoilNotificationEnabledValue(res?.active ?? false);
  }, [changeSoilNotificationEnabledValue, planId, userStore?.requestLogin]);

  const fetchSensorProperties = React.useCallback(async () => {
    if (planId == null) throw new Error("Plan id is not empty");

    const sensorProperties = await calcApi.fetchSensorProperties(
      planId,
      userStore?.requestLogin
    );
    changeSensorProperties(sensorProperties);
  }, [changeSensorProperties, planId, userStore?.requestLogin]);

  const fetchHydrawiseSettings = React.useCallback(async () => {
    if (planId == null) throw new Error("Plan id is not empty");

    const sensorProperties = await calcApi.fetchHydrawiseSettings(
      planId,
      userStore?.requestLogin
    );
    injectCircuitsWithDefaults(sensorProperties?.assigned);
    const newHydrawiseSettings = hydrawiseSettingsFactory(sensorProperties);

    changeHydrawiseSettings(newHydrawiseSettings);
  }, [changeHydrawiseSettings, planId, userStore?.requestLogin]);

  const fetchWaterFilterProperties = React.useCallback(async () => {
    if (planId == null) throw new Error("Plan id is not empty");

    const waterFilterProperties = await calcApi.fetchWaterFilterProperties(
      planId,
      userStore?.requestLogin
    );

    changeWaterFilterProperties(waterFilterProperties);
  }, [changeWaterFilterProperties, planId, userStore?.requestLogin]);

  const fetchSensorData = React.useMemo(
    () =>
      sensorSerialNumbers.length > 0
        ? async () => {
            const res = await calcApi.fetchSensorData(
              planId,
              sensorSerialNumbers,
              userStore?.requestLogin
            );
            changeSensorData(res);
          }
        : undefined,
    [changeSensorData, planId, sensorSerialNumbers, userStore?.requestLogin]
  );

  const fetchWaterFilterData = React.useMemo(
    () =>
      waterFilterSerialNumber != null
        ? async () => {
            const res = await calcApi.fetchWaterFilterData(
              planId,
              waterFilterSerialNumber,
              userStore?.requestLogin
            );
            changeWaterFilterData(res);
          }
        : undefined,
    [
      changeWaterFilterData,
      planId,
      userStore?.requestLogin,
      waterFilterSerialNumber,
    ]
  );

  const fetchFertilizerProperties = React.useCallback(async () => {
    if (planId == null) throw new Error("Plan id is not empty");

    const fertilizerProperties = await calcApi.fetchFertilizerProperties(
      planId,
      userStore?.requestLogin
    );

    changeFertilizerProperties(fertilizerProperties);
  }, [changeFertilizerProperties, planId, userStore?.requestLogin]);

  const fetchFertilizerData = React.useMemo(
    () =>
      fertilizerSerialNumber != null
        ? async () => {
            const res = await calcApi.fetchFertilizerData(
              planId,
              fertilizerSerialNumber,
              userStore?.requestLogin
            );
            changeFertilizerData(res);
          }
        : undefined,
    [
      changeFertilizerData,
      fertilizerSerialNumber,
      planId,
      userStore?.requestLogin,
    ]
  );

  // fetch plan data
  const initialFetchFn = React.useCallback(async () => {
    await Promise.all([
      fetchPlanData(),
      fetchNotificationProperties(),
      fetchSensorProperties(),
      fetchHydrawiseSettings(),
      fetchWaterFilterProperties(),
      fetchFertilizerProperties(),
    ]);
  }, [
    fetchPlanData,
    fetchNotificationProperties,
    fetchSensorProperties,
    fetchHydrawiseSettings,
    fetchWaterFilterProperties,
    fetchFertilizerProperties,
  ]);

  const fetchingStatus = useFetch(initialFetchFn);

  // fetch and polling sensor data
  usePolling(fetchSensorData, 60 * 1000);
  usePolling(fetchWaterFilterData, 60 * 1000);
  usePolling(fetchFertilizerData, 10 * 60 * 1000);

  // initial zoom
  React.useEffect(() => {
    if (data?.elements != null && initialZoomEnabled) {
      zoomToElements(data?.elements);
      changeInitalZoomEnabled(false);
    }
  }, [data, zoomToElements, initialZoomEnabled]);

  return (
    <>
      {/* Pending animation */}
      {fetchingStatus === "pending" && (
        <CalculationAnimate
          title={formatMessage({ id: "calculation.initial.title" })}
          text={formatMessage({ id: "calculation.text" })}
        />
      )}

      {/* Plan not found */}
      {(planId == null || fetchingStatus === "error") && (
        <ErrorPopup
          Icon={NotFoundSVG}
          title={formatMessage({ id: "common.not_found.title" })}
        >
          <FormattedMessage
            id="common.not_found.text"
            values={{
              link: (
                <RedirectLink href={getPlanerURL()}>
                  {formatMessage({
                    id: "common.not_found.link",
                  })}
                </RedirectLink>
              ),
            }}
          />
        </ErrorPopup>
      )}

      {/* Sensors not found */}
      {data != null && (sensorList == null || sensorList.length === 0) && (
        <ErrorPopup
          Icon={MagnifierSVG}
          title={formatMessage({ id: "common.sensor_not_found.title" })}
        >
          <FormattedMessage
            id="common.sensor_not_found.text"
            values={{
              link: (
                <RedirectLink href={getPlanerURL(planId)}>
                  {formatMessage({
                    id: "common.sensor_not_found.link",
                  })}
                </RedirectLink>
              ),
            }}
          />
        </ErrorPopup>
      )}
    </>
  );
}

export default PlanDataController;
